import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import BorderGlow from '../../atoms/BorderGlow/BorderGlow';
import { useSellerProfile } from '../../../hooks/useSellerProfile';
import { useLogoTone } from '../../../hooks/useLogoTone';
import { sellerReportUrl } from '../sellerReportUrl';
import type { SellerRef } from '../SellerActions';

/**
 * SellerCard — vertical card for the Team → Sellers grid, rendered inside a
 * BorderGlow wrapper. Logo top (enriched profile_picture, icon fallback),
 * name, seller ID. The WHOLE card opens the details modal via a stretched
 * hit button (overlay). Inline actions: Report (same generator as the
 * details-modal header button); Brand-AI lives in the details-modal header.
 * A top-right ⋯ menu holds secondary/destructive actions (Remove). Logo uses
 * the same cached profile query as the details modal.
 */

export interface SellerCardActions {
  onOpen: (seller: SellerRef) => void;
  onRemove: (seller: SellerRef) => void;
  /** Id currently being removed (disables that card's remove action). */
  removingId: string | null;
}

export const SellerCard: React.FC<{ seller: SellerRef; actions: SellerCardActions; campaignCounts?: React.ReactNode }> = ({ seller, actions, campaignCounts }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Same query cache as SellerDetailsModal — the card does not duplicate the
  // profile logic, it reads the identical `sellers.profile(id)` entry.
  const profile = useSellerProfile(seller);
  const logo = profile.data?.logo;
  const removing = actions.removingId === seller.id;
  // Adaptive tile background: sample the logo's tone once per URL and flip
  // the tile between light glass (dark logo) and dark glass (light logo).
  const logoTone = useLogoTone(logo);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <BorderGlow className="team-seller-card" borderRadius={22} glowRadius={28} glowIntensity={0.9}
      glowColor="210 90 80" colors={['#5260FF', '#22D3EE', '#c084fc']} edgeSensitivity={35} fillOpacity={0.35}>
      <div className={`team-seller-card-logo${logo && !logoFailed ? ` logo-tone-${logoTone}` : ''}`}
        data-tone={logo && !logoFailed ? logoTone : undefined}>
        {logo && !logoFailed
          ? <img src={logo} alt="" loading="lazy" onError={() => setLogoFailed(true)} />
          : <Icon icon="solar:buildings-2-linear" aria-hidden="true" />}
      </div>
      <div className="team-seller-card-body">
        <span className="team-seller-card-name" title={seller.label}>{seller.label}</span>
        <span className="team-seller-card-id" title={`Seller ID: ${seller.id}`}>ID: {seller.id}</span>
      </div>
      {campaignCounts}
      <div className="team-seller-card-actions">
        {/* Same generator the details-modal header opens (sellerReportUrl);
            a real <a> so it honours Cmd/Ctrl+click and middle-click. */}
        <a className="team-seller-card-report"
          title={`Generate the ${seller.label} company report`}
          aria-label={`Generate report for ${seller.label}`}
          href={sellerReportUrl(seller, profile.data?.name)}
          onClick={(event) => event.stopPropagation()}>
          <Icon icon="bx:file" className="h-3.5 w-3.5" aria-hidden="true" />
          Report
        </a>
      </div>
      <div className="team-seller-card-menu" ref={menuRef}>
        <button type="button" className="team-seller-card-menu-trigger" aria-label={`More actions for ${seller.label}`}
          aria-haspopup="menu" aria-expanded={menuOpen} title="More actions"
          onClick={() => setMenuOpen((open) => !open)}>
          <Icon icon="bx:dots-horizontal-rounded" aria-hidden="true" />
        </button>
        {menuOpen && (
          <div role="menu" aria-label={`Actions for ${seller.label}`} className="team-seller-card-menu-popover">
            <button type="button" role="menuitem" className="team-seller-card-menu-item-danger"
              disabled={removing} aria-label={`Remove seller ${seller.label}`}
              onClick={() => { setMenuOpen(false); actions.onRemove(seller); }}>
              <Icon icon="solar:trash-bin-trash-linear" className="h-4 w-4" aria-hidden="true" />
              {removing ? 'Removing…' : 'Remove seller'}
            </button>
          </div>
        )}
      </div>
      {/* Stretched hit target: the entire card opens the details modal. Sits
          above the card content but below the Brand button / ⋯ menu (z-3) so
          those stay clickable. */}
      <button type="button" className="team-seller-card-hit" aria-label={`View seller ${seller.label}`}
        onClick={() => actions.onOpen(seller)} />
    </BorderGlow>
  );
};
